import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';

/**
 * Audit log utilities for live session scoring.
 * Appends point-by-point scoring events to `sessions/current/events`
 * and archives them into the final session record so matches can be replayed or debugged.
 */

let memoryDeviceId = null;

export function getDeviceId() {
  if (typeof window === 'undefined') return 'server';
  try {
    let id = localStorage.getItem('padelmvp_device_id');
    if (!id) {
      id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : 'dev_' + Math.random().toString(36).substring(2, 12);
      localStorage.setItem('padelmvp_device_id', id);
    }
    return id;
  } catch (_) {
    if (!memoryDeviceId) {
      memoryDeviceId = 'dev_' + Math.random().toString(36).substring(2, 12);
    }
    return memoryDeviceId;
  }
}

export function getDeviceType() {
  if (typeof window === 'undefined') return 'server';
  try {
    if (Capacitor.isNativePlatform?.()) {
      const isTablet = /iPad|tablet/i.test(navigator.userAgent || '');
      return isTablet ? 'tablet' : 'phone';
    }
    const isMobile = /Android|iPhone|iPod/i.test(navigator.userAgent || '');
    const isTablet = /iPad/i.test(navigator.userAgent || '');
    if (isTablet) return 'tablet';
    if (isMobile) return 'phone';
    return 'web';
  } catch (_) {
    return 'web';
  }
}

let eventSequenceCounter = 0;

export function sanitizeScore(score) {
  if (!score) return null;
  return {
    sets: Array.isArray(score.sets) ? [...score.sets] : [0, 0],
    games: Array.isArray(score.games) ? [...score.games] : [0, 0],
    points: Array.isArray(score.points) ? [...score.points] : [0, 0],
    isTieBreak: Boolean(score.isTieBreak),
    setScores: Array.isArray(score.setScores)
      ? score.setScores.map(s => Array.isArray(s) ? { team1: s[0], team2: s[1] } : { team1: s?.team1 ?? 0, team2: s?.team2 ?? 0 })
      : []
  };
}

/**
 * Record a scoring event to `artifacts/{appId}/public/data/clubs/{clubId}/sessions/current/events`
 */
export async function logScoringEvent(db, appId, clubId, eventData) {
  if (!db || !appId || !clubId) return null;
  eventSequenceCounter += 1;
  const eventsCollectionRef = collection(
    db,
    'artifacts',
    appId,
    'public',
    'data',
    'clubs',
    clubId,
    'sessions',
    'current',
    'events'
  );

  const payload = {
    action: eventData.action || 'point', // 'point' | 'undo' | 'game_won' | 'set_won' | 'match_won' | 'session_start'
    device: eventData.device || getDeviceType(),
    deviceId: eventData.deviceId || getDeviceId(),
    clientTs: Date.now(),
    clientTime: new Date().toISOString(),
    ts: serverTimestamp(),
    sequence: eventSequenceCounter,
    scoringTeam: eventData.scoringTeam ?? null,
    server: eventData.server || null,
    before: sanitizeScore(eventData.before),
    after: sanitizeScore(eventData.after),
    bestOfSets: typeof eventData.bestOfSets === 'number' ? eventData.bestOfSets : 3,
    note: eventData.note || null
  };

  try {
    const docRef = await addDoc(eventsCollectionRef, payload);
    return docRef.id;
  } catch (err) {
    console.warn('[ScoringAudit] Failed to log scoring event:', err);
    return null;
  }
}

/**
 * Retrieve all scoring events from the current session's events subcollection.
 */
export async function fetchCurrentSessionEvents(db, appId, clubId) {
  if (!db || !appId || !clubId) return [];
  try {
    const eventsCollectionRef = collection(
      db,
      'artifacts',
      appId,
      'public',
      'data',
      'clubs',
      clubId,
      'sessions',
      'current',
      'events'
    );
    const q = query(eventsCollectionRef, orderBy('clientTs', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (err) {
    console.warn('[ScoringAudit] Failed to fetch session events:', err);
    return [];
  }
}

/**
 * Delete all events in `sessions/current/events` after they have been moved into archived_sessions.
 */
export async function clearCurrentSessionEvents(db, appId, clubId) {
  if (!db || !appId || !clubId) return;
  try {
    const eventsCollectionRef = collection(
      db,
      'artifacts',
      appId,
      'public',
      'data',
      'clubs',
      clubId,
      'sessions',
      'current',
      'events'
    );
    const snapshot = await getDocs(eventsCollectionRef);
    const deletions = snapshot.docs.map(docSnap => deleteDoc(doc(eventsCollectionRef, docSnap.id)));
    await Promise.all(deletions);
  } catch (err) {
    console.warn('[ScoringAudit] Failed to clear session events:', err);
  }
}
